import { Link } from 'react-router-dom';
import MigrationCrunchFooter from '../components/MigrationCrunchFooter';
import {
  Database, HandHeart, Phone, EnvelopeSimple, UploadSimple, MagnifyingGlass,
  Certificate, CheckCircle, ArrowRight, ShieldCheck, Clock
} from '@phosphor-icons/react';

// The "what exactly happens" page for the Concierge add-on. Job: remove the
// hesitation of paying hundreds of dollars for a service before knowing what
// the experience looks like, minute by minute.

const STEPS = [
  {
    icon: HandHeart,
    tag: 'Step 1 · 2 minutes',
    title: 'Book it — right on the pricing page',
    body: 'Pick your migration tier, check the "Add Concierge" box, and tell us your phone number and the call window that suits you (mornings, afternoons, evenings ET — or ASAP). One Stripe payment covers everything. No quotes, no sales call, no per-record charges.',
  },
  {
    icon: EnvelopeSimple,
    tag: 'Step 2 · within a few business hours',
    title: 'We confirm your exact call time by email',
    body: 'You get a confirmation with the specific date and time inside the window you picked, plus a short note on what to have handy. If the time doesn\u2019t work, just reply and we\u2019ll move it.',
  },
  {
    icon: Phone,
    tag: 'Step 3 · at your scheduled time',
    title: 'Your migration specialist calls you',
    body: 'A real person who does this every week. If you already have an export file, great. If you don\u2019t, they walk you through pulling one from your old CRM right on the call — that\u2019s usually the part people dread, and it takes minutes with someone guiding you.',
  },
  {
    icon: UploadSimple,
    tag: 'Step 4 · on the same call · ~20 minutes',
    title: 'The migration runs while you\u2019re on the phone',
    body: 'Your specialist uploads the export, the AI maps every field (including the ones buried in 20-year-old notes), runs the pre-flight check, and processes the migration. You don\u2019t touch a mapping screen unless you want to watch.',
  },
  {
    icon: MagnifyingGlass,
    tag: 'Step 5 · together, before anything is final',
    title: 'You spot-check 10 records together',
    body: 'Your specialist pulls up ten of YOUR contacts and you verify them together — names, phones, notes, history, all attached to the right people. Anything that looks off gets fixed on the spot, not discovered three weeks later.',
  },
  {
    icon: Certificate,
    tag: 'Step 6 · before you hang up',
    title: 'Signed audit certificate + your import file',
    body: 'You get the AI Data Integrity Certificate ("50,000 records checked — 49,982 verified, here are the ones to review") and your clean import file, downloaded or imported into your new CRM. Most migrations finish in a single 45–60 minute call.',
  },
];

const PRICING_BANDS = [
  { tier: 'Contacts Only', tierPrice: '$299', addon: '+$500', total: '$799' },
  { tier: 'Starter Pro', tierPrice: '$599', addon: '+$500', total: '$1,099' },
  { tier: 'Growth AI', tierPrice: '$1,799', addon: '+$750', total: '$2,549' },
  { tier: 'Enterprise AI', tierPrice: '$4,499', addon: '+$1,000', total: '$5,499' },
  { tier: 'Unlimited AI', tierPrice: '$7,999', addon: 'Included', total: '$7,999' },
];

const FAQS = [
  {
    q: 'What if my database is huge?',
    a: 'Same flat add-on price for your tier. There are no per-record charges and no surprise second invoice — the number you pay at checkout is the number, period.',
  },
  {
    q: 'Is my old CRM ever touched?',
    a: 'Never. We only read exports. Your old system stays exactly as it is until YOU decide to cancel it — which most people do the same week.',
  },
  {
    q: 'What if I can\u2019t figure out how to export my data?',
    a: 'That\u2019s literally what the call is for. Your specialist walks you through the export from ACT!, GoldMine, Salesforce, HubSpot, or whatever you\u2019re leaving — click by click.',
  },
  {
    q: 'Do I need to be technical?',
    a: 'If you can answer a phone, you can do this. Your specialist drives; you verify your own data at the end.',
  },
  {
    q: 'What if something doesn\u2019t look right?',
    a: 'You spot-check records together before anything is final, and every migration ships with a signed AI Data Integrity Certificate listing exactly what was verified. Problems get fixed on the call.',
  },
  {
    q: 'What if you can\u2019t complete my migration?',
    a: 'Full refund. If we can\u2019t deliver your migration, you don\u2019t pay for it — simple as that.',
  },
];

export default function ConciergeExperiencePage() {
  return (
    <div className="min-h-screen bg-[#0a0e27] text-white" data-testid="concierge-experience-page">
      {/* Top bar */}
      <header className="border-b border-white/10 px-6 py-4 sticky top-0 bg-[#0F0F1A]/95 backdrop-blur-sm z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <Link to="/migrate" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="ce-nav-logo">
            <Database size={28} weight="fill" className="text-[#F39C12]" />
            <span className="text-xl font-black">Migration<span className="text-[#F39C12]">Crunch</span></span>
          </Link>
          <Link
            to="/migrate#pricing"
            className="bg-[#F39C12] hover:bg-[#E08C00] text-black px-4 py-2 text-xs font-black rounded transition-colors"
            data-testid="ce-nav-pricing"
          >
            See pricing →
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 pt-16 pb-12">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="bg-[#F39C12] text-black text-[10px] font-black px-2 py-0.5 rounded">WE DO IT FOR YOU</span>
            <span className="text-xs text-[#F39C12] uppercase tracking-wider font-black">Concierge Migration Add-On</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black leading-tight mb-4" data-testid="ce-headline">
            Your entire migration,<br />done on <span className="text-[#F39C12]">one phone call.</span>
          </h1>
          <p className="text-white/70 text-base md:text-lg leading-relaxed mb-6">
            Add Concierge to any MigrationCrunch tier and a migration specialist runs the
            whole thing <em>with</em> you — you never touch a mapping screen. Here is exactly
            what happens after you click pay, minute by minute.
          </p>
          <div className="flex items-center justify-center gap-4 text-[11px] text-white/50">
            <span className="flex items-center gap-1"><Clock size={13} weight="bold" className="text-emerald-300" /> Usually one 45–60 min call</span>
            <span className="flex items-center gap-1"><ShieldCheck size={13} weight="bold" className="text-emerald-300" /> Old CRM never touched</span>
            <span className="flex items-center gap-1"><CheckCircle size={13} weight="bold" className="text-emerald-300" /> Flat price, no per-record charges</span>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="px-6 pb-16">
        <div className="max-w-3xl mx-auto space-y-4">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="flex gap-4 bg-white/5 border border-white/10 rounded-lg p-5" data-testid={`ce-step-${i + 1}`}>
                <div className="shrink-0 w-11 h-11 rounded-full bg-[#F39C12]/15 border border-[#F39C12]/40 flex items-center justify-center">
                  <Icon size={20} weight="fill" className="text-[#F39C12]" />
                </div>
                <div>
                  <div className="text-[10px] font-black text-[#F39C12] uppercase tracking-widest mb-1">{s.tag}</div>
                  <h3 className="font-black text-base mb-1">{s.title}</h3>
                  <p className="text-sm text-white/70 leading-relaxed">{s.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* What you'll need */}
      <section className="px-6 pb-16">
        <div className="max-w-3xl mx-auto bg-emerald-500/[0.06] border border-emerald-400/30 rounded-lg p-6" data-testid="ce-what-you-need">
          <h2 className="text-lg font-black mb-3 text-emerald-300">What you&apos;ll need ready: almost nothing</h2>
          <ul className="text-sm text-white/75 space-y-2">
            <li>✓ <strong>A phone</strong> — we call you, at the window you picked</li>
            <li>✓ <strong>Access to your old CRM</strong> (or an export file if you already have one — either works)</li>
            <li>✓ That&apos;s it. No spreadsheet cleanup, no field mapping homework, no IT person.</li>
          </ul>
        </div>
      </section>

      {/* Pricing */}
      <section className="px-6 pb-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-black text-center mb-2">One flat price on top of your tier</h2>
          <p className="text-center text-white/50 text-sm mb-8">No per-record charges. No overage invoices. The checkout number is the final number.</p>
          <div className="overflow-x-auto rounded-lg border border-white/15" data-testid="ce-pricing-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/10 text-left">
                  <th className="px-4 py-3 font-black">Your tier</th>
                  <th className="px-4 py-3 font-black">Tier price</th>
                  <th className="px-4 py-3 font-black">Concierge add-on</th>
                  <th className="px-4 py-3 font-black">Total, done for you</th>
                </tr>
              </thead>
              <tbody>
                {PRICING_BANDS.map((b, i) => (
                  <tr key={i} className="border-t border-white/10" data-testid={`ce-band-${i}`}>
                    <td className="px-4 py-3 font-bold">{b.tier}</td>
                    <td className="px-4 py-3 text-white/70">{b.tierPrice}</td>
                    <td className={`px-4 py-3 font-bold ${b.addon === 'Included' ? 'text-emerald-300' : 'text-[#F39C12]'}`}>{b.addon}</td>
                    <td className="px-4 py-3 font-black">{b.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-white/40 text-center mt-3">
            One booking = one database, one scheduled migration. Consultants moving multiple client databases: book one Concierge per database.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 pb-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-black text-center mb-8">The questions everyone asks before paying</h2>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-5" data-testid={`ce-faq-${i}`}>
                <h3 className="font-black text-sm mb-1.5 text-[#F39C12]">{f.q}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 pb-20">
        <div className="max-w-3xl mx-auto text-center bg-gradient-to-r from-[#F39C12]/15 to-transparent border border-[#F39C12]/50 rounded-lg p-8">
          <h2 className="text-2xl font-black mb-2">Ready to never think about this again?</h2>
          <p className="text-white/70 text-sm mb-6">
            Pick your tier, check the <strong className="text-[#F39C12]">&ldquo;Add Concierge&rdquo;</strong> box,
            and choose when we should call. That&apos;s the whole job — we take it from there.
          </p>
          <Link
            to="/migrate#pricing"
            className="inline-flex items-center gap-2 bg-[#F39C12] hover:bg-[#E08C00] text-black px-8 py-3 text-sm font-black rounded transition-colors"
            data-testid="ce-cta-pricing"
          >
            Choose my tier + add Concierge <ArrowRight size={15} weight="bold" />
          </Link>
          <p className="text-[11px] text-white/40 mt-4">If we can&apos;t complete your migration, you get a full refund.</p>
        </div>
      </section>

      <MigrationCrunchFooter />
    </div>
  );
}
